<script>
import { AdminPageType } from '@/consts/admin'
import { useAdminPageAccess } from '@/composables/useAdminPageAccess'
import Vue from 'vue'
import { mapState, mapGetters } from 'vuex'
import adminUserApi from '@/api/adminUser'
import adminWashRequestApi from '@/api/adminWashRequest'
import adminWashRequestAddStaffModal from '@/components/Admin/AdminWashRequestAddStaffModal.vue'
import adminWashRequestDelayNoticeModal from '@/components/Admin/AdminWashRequestDelayNoticeModal.vue'
import {
  PICKUP_MAX_COUNT,
  createWashRequestViewModel,
  getWashedOrPickedUpHighlightClass,
  shouldShowFoldTypeSettingConfirmation,
  isBeforePickup,
  isFolded,
} from '@/utils/washRequestUtils'
import { getHasOilTreatmentText, getLaundryTypeTextByMap } from '@/utils/oilAndLaundryTypeUtils'

export default {
  components: {
    adminWashRequestAddStaffModal,
    adminWashRequestDelayNoticeModal,
  },
  name: 'admin-wash-requests-change',
  props: {
    id: {
      type: [String, Number],
      default: -1
    },
    searchPrm: {
      type: Object,
      'default': () => ({
        staffId: '',
        createdDtFrom: '',
        createdDtTo: '',
        filterWreqCreated: false,
        filterPickedUp: false,
        filterWashed: false,
        filterFolded: false,
        filterDelivered: false,
        userId: '',
        bagSize:'',
      })
    },
    adminType: {
      type: String,
      default: AdminPageType.ADMIN
    }
  },
  data() {
    return {
      pdTypeMap: {},
      pdStatusTypeMap: {},
      laundryTypeMap: {},

      loginUser: {},
      wreq: {},
      staffId: '',
      staffs: [],
      pdType: 0,
      isAbsenceMailSent: false,
      number: '',
      code: '',
      count: '',
      isOnetime: false,

      showStaffModal: false,
      showPrevStatusBtn: true,
      showNextStatusBtn: true,
      showPrevStatusModal: false,
      showNextStatusModal: false,
      showAddStaffModal: false,
      showUpdateCountModal: false,
      showDelayNoticeModal: false,

      canAddStaff: false,

      isProcessing: false,

      selectedOilTreatment: '',
      selectedLaundryType: '',
      foldTypeConfirm: false,

      planMap: {},
      sizeMap: {},
      optionMap: {},

      errors: {},
      serverErrors: [],
    }
  },
  beforeRouteLeave (to, from, next) {
    // ブラウザバック時にパラメータがないため追加
    to.params.searchPrm = this.searchPrm
    next()
  },
  computed: {
    ...mapState('user', {
      userId: state => state.id,
    }),
    ...mapGetters('user', [
      'isStaffTypeManagerOrLeader',
      'isStaffTypeWashFold',
      'isStaffTypePickupDelivery',
    ]),
    selectCount() {
      return Array.from({ length: PICKUP_MAX_COUNT }, (_, i) => i + 1)
    },
    isAdmin() {
      return this.adminType === AdminPageType.ADMIN
    },
    isStaffPage() {
      return this.adminType === AdminPageType.STAFF
    },
    isLeaderAbove() {
      return this.isAdmin || (this.isStaffPage && this.isStaffTypeManagerOrLeader)
    },
    isExceptPickupDelivery() {
      return this.isAdmin || (this.isStaffPage && !this.isStaffTypePickupDelivery)
    },
    isExceptWashFold() {
      return this.isAdmin || (this.isStaffPage && !this.isStaffTypeWashFold)
    },
    hasError() {
      return Object.keys(this.errors).length > 0
    },
    pickupDeliveryTypeDisp() {
      const obj = this.pdTypeMap[this.pdType]
      return obj ? obj.val : ''
    },
    needsOilTreatmentAndLaundryTypeSelection() {
      return this.currentResultDtProp === 'result_wash_dt' &&
        this.wreq.result_pickup_dt &&
        (this.wreq.has_oil_treatment === null || this.wreq.laundry_type === null)
    },
    statusHighlightClass() {
      return getWashedOrPickedUpHighlightClass(this.wreq)
    },
    isModalCloseButtonDisabled() {
      return this.isProcessing || (shouldShowFoldTypeSettingConfirmation(this.wreq) && !this.foldTypeConfirm)
    },
    shouldShowAbsencePickupButton() {
      return this.isExceptWashFold && isBeforePickup(this.wreq)
    },
    shouldShowAbsenceDeliveryButton() {
      return this.isExceptWashFold && isFolded(this.wreq)
    },
    shouldShowDelayNoticeButton() {
      return this.isExceptWashFold && (isBeforePickup(this.wreq) || isFolded(this.wreq))
    },
  },
  async mounted() {
    const { ensureStaffAccess, ensureManagerAccessInMaintenance } = useAdminPageAccess(this.adminType)
    const canAccess = await ensureStaffAccess()
    if (!canAccess) return
    if (!await ensureManagerAccessInMaintenance()) return

    const me = await this.$store.dispatch('user/getMe')
    this.loginUser.id = me.id
    this.loginUser.name = me.name1 + ' ' + me.name2

    const mst = await window.master.$promise
    this.planMap = mst.planMap
    this.sizeMap = mst.lovsMap.size
    this.pdTypeMap = mst.lovsMap.pd_type
    this.pdStatusTypeMap = mst.lovsMap.pd_status_type
    this.optionMap = mst.lovsMap.user_plan_option
    this.laundryTypeMap = mst.lovsMap.laundry_type

    this.getWashRequest()
    const { data: staffsData } = await adminUserApi.myOfficeStaffs()
    this.staffs = staffsData
  },
  methods: {
    getLaundryTypeTextByMap,
    getHasOilTreatmentText,
    shouldShowFoldTypeSettingConfirmation,
    async getWashRequest() {
      const { data: washRequestData } = await adminWashRequestApi.getWashRequest(this.id)
      this.wreq = await this.convWashRequest(washRequestData)
      this.staffId = washRequestData.staff_id

      this.canAddStaff = this.wreq.status_key == 2 && this.wreq.count >= 1 && this.wreq.size_key != "1"

      const { data: user } = await adminUserApi.getUser(this.wreq.user_id)
      if (user.user_plan) {
        this.pdType = user.user_plan.pickup_delivery_type
      }
    },
    async convWashRequest(wreq) {
      const { data } = await adminUserApi.myOfficeStaffs()
      const staffs = data.filter(e => wreq.staff_id === e.id)
      const ret = createWashRequestViewModel(wreq, staffs[0])

      this.isOnetime = ret.isOnetimePlan
      if (!this.isOnetime) {
        ret.size = this.sizeMap[this.planMap[wreq.plan_id].size].val + 'サイズ'
      }
      ret.size_key = this.planMap[wreq.plan_id].size
      let option = this.optionMap[wreq.user_plan_option]
      ret.option = option ? option.val : 'なし'

      // 未集荷
      this.showPrevStatusBtn = false;
      if (this.isExceptWashFold) {
        this.showNextStatusBtn = true;
      } else {
        this.showNextStatusBtn = false;
      }
      ret.prev_status = this.pdStatusTypeMap[0].val
      ret.status = this.pdStatusTypeMap[0].val
      ret.status_key = 0
      ret.next_status = this.pdStatusTypeMap[1].val
      this.prevResultDtProp = ''
      this.currentResultDtProp = 'result_pickup_dt'

      if (wreq.result_pickup_dt) {
        // 集荷済
        if (this.isExceptPickupDelivery) {
          this.showPrevStatusBtn = true;
        } else {
          this.showPrevStatusBtn = false;
        }
        if (this.isLeaderAbove || (this.isStaffTypeWashFold && (wreq.staff_id === this.userId || wreq.staff_id === null))) {
          this.showNextStatusBtn = true;
        } else {
          this.showNextStatusBtn = false;
        }
        ret.prev_status = this.pdStatusTypeMap[0].val
        ret.status = this.pdStatusTypeMap[1].val
        ret.status_key = 1
        ret.next_status = this.pdStatusTypeMap[2].val
        this.prevResultDtProp = 'result_pickup_dt'
        this.currentResultDtProp = 'result_wash_dt'
      }
      if (wreq.result_wash_dt) {
        // 洗濯済
        if (this.isExceptPickupDelivery) {
          this.showPrevStatusBtn = true;
        } else {
          this.showPrevStatusBtn = false;
        }
        if (this.isLeaderAbove || (this.isStaffTypeWashFold && (wreq.staff_id === this.userId || wreq.staff_id === null))) {
          this.showNextStatusBtn = true;
        } else {
          this.showNextStatusBtn = false;
        }
        ret.prev_status = this.pdStatusTypeMap[1].val
        ret.status = this.pdStatusTypeMap[2].val
        ret.status_key = 2
        ret.next_status = this.pdStatusTypeMap[4].val
        this.prevResultDtProp = 'result_wash_dt'
        this.currentResultDtProp = 'result_fold_dt'
      }
      if (wreq.result_fold_dt) {
        // たたみ済
        if (this.isExceptPickupDelivery) {
          this.showPrevStatusBtn = true;
        } else {
          this.showPrevStatusBtn = false;
        }
        if (this.isExceptWashFold) {
          this.showNextStatusBtn = true;
        } else {
          this.showNextStatusBtn = false;
        }
        ret.prev_status = this.pdStatusTypeMap[2].val
        ret.status = this.pdStatusTypeMap[4].val
        ret.status_key = 4
        ret.next_status = this.pdStatusTypeMap[3].val
        this.prevResultDtProp = 'result_fold_dt'
        this.currentResultDtProp = 'result_delivery_dt'
      }
      if (wreq.result_delivery_dt) {
        // 配達済
        if (this.isExceptPickupDelivery) {
          this.showPrevStatusBtn = true;
        } else {
          this.showPrevStatusBtn = false;
        }
        this.showNextStatusBtn = false;
        ret.prev_status = this.pdStatusTypeMap[4].val
        ret.status = this.pdStatusTypeMap[3].val
        ret.status_key = 3
        ret.next_status = this.pdStatusTypeMap[3].val
        this.prevResultDtProp = 'result_delivery_dt'
      }

      if (ret.absence_mail_histories) {
        this.isAbsenceMailSent = true;
      }

      return ret
    },
    showPrevStatus() {
      this.showPrevStatusModal = true
    },
    showNextStatus() {
      if (this.canAddStaff) {
        this.showAddStaffModal = true
        document.body.style.overflow = 'hidden';
      } else {
        this.showNextStatusModal = true
        const dtFormat = Vue.filter('dtFormat')
        this.dtStr = dtFormat(new Date(), 'yyyy-mm-ddTHH:MM')
        this.count = '' // 個数を指定できる場合、スタッフに必ず選択させる
        this.foldTypeConfirm = false

        if (this.needsOilTreatmentAndLaundryTypeSelection) {
          this.selectedOilTreatment = this.wreq.has_oil_treatment !== null ? String(this.wreq.has_oil_treatment) : ''
          this.selectedLaundryType = this.wreq.laundry_type !== null ? String(this.wreq.laundry_type) : ''
        }
      }
    },
    sendAbsenceMail(type) {
      const confirmMessage = this.isAbsenceMailSent ? '不在メールを再送信しますか？' : '不在メールを送信しますか？'
      const resultMessage = this.isAbsenceMailSent ? '不在メールを再送信しました。' : '不在メールを送信しました。'
      if (!confirm(confirmMessage)) {
        return
      }

      const reqObj = {
        type: type,
      }
      adminWashRequestApi.sendAbsenceMail(this.id, reqObj).then(() => {
        this.isAbsenceMailSent = true
        alert(resultMessage)
      })
    },
    moveToNextStatus() {
      if (this.wreq.status_key === 0) {
        this.updateCountAndDate()
      } else {
        this.setDate()
      }
    },
    async updateCountAndDate() {
      // 未集荷=>集荷 への遷移時、個数更新と実集荷日時更新を行う処理.
      // 呼び出し側で分岐させているが、念のためこちら側でもチェック
      if (this.wreq.status_key !== 0) { return }

      if (!this.validateUpdateCountAndDate()) { return }

      // 個数更新の際、ステータスが集荷済み以降でないとメールが送信されないので、先にステータスを更新する.

      // 実集荷日時更新
      const dt = this.dateStringToDate(this.dtStr)
      const dtFormat = Vue.filter('dtFormat')
      // this.setDate() だとnumber,codeやstaffListを入れるところがあるが、
      // staff_listは 洗濯済=>たたみ の場合、
      // number,codeは　たたみ済み=>配達 の場合、
      // でしか入らないので、ここでは考慮不要.
      await adminWashRequestApi.setDate(this.id, {
        result_pickup_dt: dtFormat(dt, 'yyyy-mm-dd HH:MM:SS'),
        count: this.count,
      })

      // 個数更新
      try {
        await adminWashRequestApi.updateCount(this.id, {
          count: this.count,
        })
      } catch (e) {
        alert('集荷済みにしましたが、個数の変更に失敗しました。再度個数の変更を行ってください。')
        this.getWashRequest()
        this.showNextStatusModal = false
        return
      }

      this.afterSetDateSuccess()
    },
    async setDate(staffList = [], dtStr = this.dtStr) {
      this.validateSetDate()
      if (this.hasError) return

      const dt = this.dateStringToDate(dtStr)
      const dtFormat = Vue.filter('dtFormat')
      const obj = {
        [this.currentResultDtProp]: dtFormat(dt, 'yyyy-mm-dd HH:MM:SS'),
        'number': this.number,
        'code': this.code,
      }
      if (staffList) {
        obj.staff_list = staffList
      }

      // 集荷済み→洗濯済みで未設定の場合はoil_treatmentとlaundry_typeを追加
      if (this.needsOilTreatmentAndLaundryTypeSelection) {
        obj.has_oil_treatment = Number(this.selectedOilTreatment)
        obj.laundry_type = Number(this.selectedLaundryType)
      }

      this.isProcessing = true;
      try {
        await adminWashRequestApi.setDate(this.id, obj)
        this.afterSetDateSuccess(staffList)
      } catch (error) {
        console.error('Failed to set date:', error)
      } finally {
        this.isProcessing = false;
      }
    },
    dateStringToDate(dateString) {
      const dtArr = dateString.replace('T', ' ').split(/[- :+]/)
      return new Date(dtArr[0], dtArr[1] - 1, dtArr[2], dtArr[3], dtArr[4])
    },
    afterSetDateSuccess(staffList) {
      this.getWashRequest()
      this.showNextStatusModal = false
      if (staffList) {
        // FIXME これは一体なにをしてるんだろうか？もっとマシな方法があるはず
        document.body.style.overflow = ''
      }
      this.backToWreqList()
    },
    delDate() {
      const obj = {
        [this.prevResultDtProp]: 1,
      }
      adminWashRequestApi.delDate(this.id, obj).then(() => {
        this.getWashRequest()
        this.showPrevStatusModal = false
        this.backToWreqList()
      })
    },
    assignStaff() {
      if (!this.validateAssignStaff()) { return }

      const obj = {
        staff_id: this.staffId
      }
      adminWashRequestApi.assignStaff(this.id, obj).then(({ data }) => {
        this.getWashRequest()
        this.showStaffModal = false
      }).catch(e => {
        const errRes = e.response.data.errors || []
        let serverErrors = []
        Object.keys(errRes).forEach(k => {
          serverErrors = serverErrors.concat(errRes[k])
        })
        this.serverErrors = serverErrors;
      })
    },
    showUpdateCount() {
      this.showUpdateCountModal = true
      this.count = this.wreq.count
    },
    updateCount() {
      if (!this.validateUpdateCount()) { return }

      const pattern = /^[1-9]\d?$|^0$|^100$/
      if (!pattern.test(this.count)) { return }

      const obj = {
        ['count']: this.count
      }
      adminWashRequestApi.updateCount(this.id, obj).then(({ data }) => {
        this.getWashRequest()
        this.showUpdateCountModal = false
      }).catch(e => {
        const errRes = e.response.data.errors || []
        let serverErrors = []
        Object.keys(errRes).forEach(k => {
          serverErrors = serverErrors.concat(errRes[k])
        })
        this.serverErrors = serverErrors;
      })
    },
    validateAssignStaff() {
      this.errors = {}
      let result = true

      if (!this.staffId) {
        this.errors['staffId_required'] = true
        result = false
      }

      return result
    },
    validateUpdateCount() {
      this.errors = {}
      let result = true

      if (!this.count) {
        this.errors['count_required'] = true
        result = false
      }

      return result
    },
    validateSetDate() {
      this.errors = {}

      if (this.needsOilTreatmentAndLaundryTypeSelection) {
        if (!this.selectedOilTreatment || !this.selectedLaundryType) {
          this.errors.oil_treatment_and_laundry_type_required = true
        }
      }
      if (shouldShowFoldTypeSettingConfirmation(this.wreq) && !this.foldTypeConfirm) {
        this.errors.fold_type_confirm_required = true
      }
    },
    validateUpdateCountAndDate() {
      this.errors = {}
      let result = true

      if (!this.dtStr) {
        this.errors['dtStr_required'] = true
        result = false
      }

      if (!this.count || !this.count.match(/^[1-9]\d*$/)) {
        this.errors['count_required_on_status_key0'] = true
        result = false
      }

      return result
    },
    backToWreqList() {
      let name = 'admin-wash-requests'
      if (this.adminType === AdminPageType.STAFF) {
        name = 'staff-wash-requests'
      }
      this.$router.push({
        name: name,
        params: {
          id: this.id,
          searchPrm: this.searchPrm,
        }
      })
    },
    closeModal() {
      this.showAddStaffModal = false
      document.body.style.overflow = '';
    }
  }
}
</script>

<template src="./template.html"></template>
<style lang="scss" src="./style.scss" scoped></style>
